import { computed, ref } from 'vue'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useUserStore } from '@/stores/UserStore'
import { batchesListPath } from '@/helpers/batch'
import { isOnboardingStepComplete, onboardingProgressOf, onboardingStepProgress } from '@/helpers/onboarding'
import { safeCapture } from '@/helpers/posthog'
import { ONBOARDING_STEP_STATUS, POSTHOG_EVENTS, TOAST_TYPES } from '@/constants'

// The Getting Started steps as the dealer sees them: numbered, the finished ones marked, the order steps locked until the first sale.
// They come from Strapi. Until they load — and for good if Strapi is down — there are none, and every Get started
// surface stays hidden (`done` reads true with nothing to do) rather than showing an empty card.
export function useGetStarted () {
  const globalStore = useGlobalStore()
  const strapiStore = useStrapiStore()
  const userStore = useUserStore()

  strapiStore.fetchGetStartedSteps()
  // Unlocks the order steps; asks once, and never again after the first sale is on the record.
  userStore.checkFirstSale()

  // What this dealer has done (`data.about.onboarding`); each step's status is worked out from its tasks.
  // The dev toggle can show the record as empty, so every step is on the page.
  const progress = computed(() => onboardingProgressOf(globalStore.freshProgress ? null : userStore.user))
  // The dev toggles still pretend a sale, or the first steps done, on top of the real progress.
  const hasSale = computed(() => !!progress.value.first_sale_at || globalStore.hasSale)

  const steps = computed(() => strapiStore.getStartedSteps.map((step, index) => {
    const { tasks, status } = onboardingStepProgress(step, progress.value)
    const completed = isOnboardingStepComplete(step, progress.value, globalStore.completedSteps)

    return {
      ...step,
      tasks,
      status: completed ? ONBOARDING_STEP_STATUS.COMPLETE : status,
      number: index + 1,
      completed,
      locked: !!step.locked && !hasSale.value,
    }
  }))
  // Every step Strapi holds, the order steps included: "0 of 7" from the start. The locked ones can't be done before
  // the first sale, but they are still part of what Getting Started asks for.
  const stepCount = computed(() => steps.value.length)
  const completedCount = computed(() => steps.value.filter((step) => step.completed).length)
  // The current step is the first one still to do.
  const activeKey = computed(() => steps.value.find((step) => !step.completed && !step.locked)?.key)
  // Every step that can be done is done: Get started leaves Home, and the hub's section reads Completed.
  // Locked steps don't hold it open, or a dealer without a sale could never clear it.
  const done = computed(() => steps.value.every((step) => step.completed || step.locked))

  // The step's page in the app; the batches list lives at a design-dependent path the constants can't read.
  const stepRoute = (step) => (step.route === '/batches' ? batchesListPath() : step.route)

  // A step still to do can be marked complete by hand; the mark is written for the new design only, so it is offered there only.
  const canComplete = (step) => !step.completed && !step.locked && userStore.isNewDesign

  // The key of the step whose mark is being written, so the badge that was pressed holds still until it lands.
  const completing = ref(null)

  // "Mark as complete", from a card or the step's page. The toast says what happened, as a card leaves its carousel.
  const completeStep = async (step, surface) => {
    completing.value = step.key

    try {
      const marked = await userStore.completeOnboardingStep(step.key)
      if (!marked) return

      safeCapture(POSTHOG_EVENTS.GETTING_STARTED_STEP_MARKED_COMPLETE, {
        step_key: step.key,
        surface,
        tasks_completed: step.tasks.filter((task) => task.completed).length,
        tasks_total: step.tasks.length,
      })
      globalStore.showToast({ title: `${step.title} marked as complete`, type: TOAST_TYPES.COMPLETED })
    } finally {
      completing.value = null
    }
  }

  return { steps, stepCount, completedCount, activeKey, done, stepRoute, canComplete, completeStep, completing }
}
